<template>
  <v-btn
    :class="buttonClass"
    :color="props.color"
    :variant="props.variant"
    :disabled="isLoading || passthroughDisabled"
    v-bind="buttonAttrs"
  >
    <span class="app-button-flat__content">
      <v-progress-circular
        v-if="isLoading"
        indeterminate
        :size="props.size === 'small' ? 16 : 18"
        :width="2"
        class="app-button-flat__spinner"
      />
      <slot />
    </span>
  </v-btn>
</template>

<script setup lang="ts">
import { computed, useAttrs } from "vue";

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    size?: "default" | "small";
    color?: "primary" | "secondary" | "error" | "success" | "info" | "warning" | "text";
    variant?: "flat" | "outlined" | "tonal" |"text";
  }>(),
  {
    size: "default",
    color: "primary",
    variant: "flat",
  },
);

const attrs = useAttrs();

const isLoading = computed(() => Boolean(attrs.loading));
const passthroughDisabled = computed(() => Boolean(attrs.disabled));

const buttonClass = computed(() => [
  "app-button-flat",
  props.size === "small" ? "app-button-flat--small" : null,
  `app-button-flat--${props.color}`,
  props.variant === "outlined" ? "app-button-flat--outlined" : null,
  attrs.class,
]);

const buttonAttrs = computed(() => {
  // Vuetify icon props come through attrs as camelCase (e.g. prependIcon for prepend-icon).
  // When loading, we hide any prepend/append icons so the spinner is the only leading visual.
  const { class: _class, loading, disabled, ...rest } = attrs as Record<string, unknown>;

  if (isLoading.value) {
    delete (rest as any).prependIcon;
    delete (rest as any).appendIcon;
    // Be defensive in case attrs preserve kebab-case in some environments.
    delete (rest as any)["prepend-icon"];
    delete (rest as any)["append-icon"];
  }

  return rest;
});
</script>

<style scoped>
.app-button-flat {
  height: 48px !important;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  text-transform: capitalize;
}

.app-button-flat--small {
  height: 42px;
  font-size: 13px;
  padding: 0 12px;
  font-weight: 600;
}

.app-button-flat--primary {
  background: #e8612f;
  color: #fff;
}

.app-button-flat--secondary {
  background: #f1f5f9;
  color: #0f172a;
}

.app-button-flat--dark {
  background: #0f172a;
  color: #fff;
}

.app-button-flat--light {
  background: #ffffff;
  color: #0f172a;
  border: 1px solid #e2e8f0;
}

.app-button-flat--outlined {
  background: transparent;
  border: 1px solid currentColor;
}

.app-button-flat__content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.app-button-flat__spinner {
  color: currentColor;
}
</style>
